import { addDoc, collection, doc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { firestore, isFirebaseConfigured } from "@/integrations/firebase/client";
import { getStoredUtm } from "@/lib/lead-attribution";

/** Registra un clic en un botón de conversión (CTAs, WhatsApp flotante) en DOS lugares, porque
 *  responden preguntas distintas:
 *
 *   - Firestore (`analytics/ctaClicks`): un contador acumulado por botón, que el staff ve de un
 *     vistazo en Panel CP → Estadísticas. Simple y sin depender de nada externo, pero no tiene
 *     fecha: solo dice "cuántos en total desde siempre".
 *   - GA4 (evento `cta_click`): el mismo clic con fecha, página de origen y dispositivo, que es
 *     lo que permite comparar períodos y armar el embudo (cuántos llegan a /contacto vs. cuántos
 *     envían el formulario). Antes esto no se mandaba, así que no había forma de saber qué botón
 *     traía clases agendadas ni si un cambio de texto mejoraba o empeoraba.
 *
 *  Fire-and-forget en ambos casos: un clic real a WhatsApp/agendar nunca debe depender de que
 *  esto funcione. Las reglas de Firestore permiten la escritura del contador sin sesión (son
 *  visitantes anónimos del sitio público) — ver el comentario en firestore.rules, donde además
 *  hay que sumar cada clave nueva a la lista blanca o esa escritura se rechaza en silencio.
 *
 *  `extraParams` (opcional) va SOLO al evento de GA4, nunca al contador de Firestore — ese
 *  contador es un total simple por botón (ver arriba), no admite dimensiones extra. Se usa para
 *  distinguir el mismo botón disparado desde distintas páginas (ej. `sede: "nunoa"` vs.
 *  `sede: "general"` en el CTA de agendar clase, compartido entre /clases y las páginas de
 *  sede) sin tener que crear una `key` nueva por página — así se compara conversión por
 *  página/sede filtrando por este parámetro en GA4, en vez de solo por `page_path`. */
export function trackCtaClick(key: string, extraParams?: Record<string, string>) {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (w.gtag) {
    w.gtag("event", "cta_click", { cta: key, page_path: window.location.pathname, ...extraParams });
  }
  if (!isFirebaseConfigured) return;
  setDoc(doc(firestore, "analytics", "ctaClicks"), { [key]: increment(1), updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
}

/** Eventos GA4 propios (Fase 3 del CRM de leads), ADICIONALES al `cta_click` genérico de
 *  arriba — no lo reemplazan, cada botón sigue mandando su `cta_click` igual que siempre. Estos
 *  dos existen aparte porque miden algo más específico que "se tocó un botón": `click_whatsapp`
 *  es cualquier clic que abre WhatsApp desde el sitio público (con `ubicacion` identificando
 *  cuál), y `generate_lead` es el momento exacto en que se genera un lead real (al enviar un
 *  formulario), que es lo que de verdad importa para medir conversión — no todo `cta_click`
 *  termina generando un lead (ej. "Conoce el método" no debería contar ahí). */
export function trackWhatsappClick(ubicacion: string) {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (w.gtag) w.gtag("event", "click_whatsapp", { ubicacion, page_path: window.location.pathname });
}

export function trackGenerateLead(extraParams?: Record<string, string>) {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (w.gtag) w.gtag("event", "generate_lead", { page_path: window.location.pathname, ...extraParams });
}

/** Registra un lead GENÉRICO (sin nombre/teléfono reales todavía) cuando alguien toca un botón de
 *  WhatsApp de cero fricción (flotante, footer, canales directos) — sin esto esa visita quedaba
 *  invisible para el CRM (ni aviso al staff, ni Métricas de Leads, solo un contador sin fecha).
 *  El staff completa nombre/teléfono reales al ver quién escribió. Fire-and-forget: nunca
 *  bloquea la navegación. Un solo lead por canal y programa por sesión del navegador
 *  (sessionStorage) para que varios toques seguidos no inunden el CRM. */
export function registerDirectWhatsappLead(canal: string, programa: "cp" | "kidstenia" = "cp") {
  if (!isFirebaseConfigured) return;
  const guardKey = `cp_direct_wa_lead_${programa}_${canal}`;
  try {
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");
  } catch { /* sin sessionStorage: se registra igual */ }
  const stored = getStoredUtm();
  addDoc(collection(firestore, "leads"), {
    nombre: "Contacto directo por WhatsApp (completar al responder)",
    telefono: "Sin dato — ver conversación de WhatsApp",
    origen: stored?.ref ? "referido" : "whatsapp_directo",
    origenDetalle: `${programa === "kidstenia" ? "Kidstenia" : "Sitio web"} — ${canal}`,
    interes: "no_sabe",
    ...(programa === "kidstenia" ? { programa: "kidstenia" } : {}),
    estado: "nuevo",
    preferenciaContacto: "whatsapp",
    ...(stored?.ref ? { referidoPorAlumnoId: stored.ref } : {}),
    ...(stored?.utmSource ? { utmSource: stored.utmSource } : {}),
    ...(stored?.utmMedium ? { utmMedium: stored.utmMedium } : {}),
    ...(stored?.utmCampaign ? { utmCampaign: stored.utmCampaign } : {}),
    creadoEn: serverTimestamp(),
  }).catch((err) => console.error("[CP] No se pudo registrar el lead directo de WhatsApp", err));
}

/** Agrega un código corto de origen al final del texto YA prellenado de un link wa.me (ej. el
 *  del botón flotante, cuyo mensaje viene del contenido editable en /admin — por eso se
 *  construye leyendo el href ya armado, en vez de rehacerlo desde cero) — para saber desde qué
 *  botón del sitio arrancó la conversación de WhatsApp sin tener que adivinar por contexto. No
 *  cambia nada visible del sitio público: el código solo se ve DENTRO de WhatsApp, en el
 *  mensaje ya escrito, nunca en la página. Si el href no tiene "text=" (o no es una URL válida),
 *  lo devuelve tal cual, sin romper el link. */
export function appendWhatsappOriginCode(href: string, code: string): string {
  try {
    const url = new URL(href);
    const text = url.searchParams.get("text");
    if (text === null) return href;
    url.searchParams.set("text", `${text} ${code}`);
    return url.toString();
  } catch {
    return href;
  }
}

export const KIDSTENIA_CTA_LABELS: Record<string, string> = {
  kidstenia_hero_outline: "Hero — \"Conoce el programa\"",
  kidstenia_hero_whatsapp: "Hero — \"Agenda una clase de prueba\"",
  kidstenia_cta_final: "CTA final — \"Agendar clase de prueba\"",
  kidstenia_whatsapp_float: "Botón flotante de WhatsApp",
};

export const CP_CTA_LABELS: Record<string, string> = {
  cp_whatsapp_float: "Botón flotante de WhatsApp (sitio principal)",
  cp_hero_agendar: "Inicio · Hero — \"Agenda tu primera clase gratis\"",
  cp_hero_metodo: "Inicio · Hero — \"Conoce el método CP\"",
  cp_home_comenzar: "Inicio · Método — \"Quiero mi clase gratis\"",
  cp_home_planes: "Inicio · Planes — \"Saber más\"",
  cp_home_cierre_agendar: "Inicio · Cierre — \"Agendar clase\"",
  cp_home_cierre_whatsapp: "Inicio · Cierre — \"WhatsApp\"",
  cp_contacto_form_submit: "Contacto — formulario enviado",
  // Compartido entre /clases y las páginas de sede — el parámetro `sede` de GA4 ("general" /
  // "nunoa" / "san-miguel") distingue el origen, este total de Firestore es la suma de las tres.
  cp_agendar_clase: "Clases/Sede — \"Agendar clase\" o \"Reservar\"",
};
